import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

interface WishlistContextValue {
  ids: number[];
  toggle: (id: number) => void;
  isInWishlist: (id: number) => boolean;
  clear: () => void;
}

const WishlistContext = createContext<WishlistContextValue | undefined>(undefined);

const STORAGE_KEY = 'wishlist_items';

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [ids, setIds] = useState<number[]>([]);
  const { user, token } = useAuth();

  useEffect(() => {
    const load = async () => {
      if (user && token) {
        try {
          const res = await fetch(`${API_BASE_URL}/me/wishlist`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.ok) {
            const data = await res.json();
            const productIds = Array.isArray(data) ? data : data.productIds;
            if (Array.isArray(productIds)) {
              setIds(productIds.map((x) => Number(x)));
            }
          }

          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (e) {
            console.error('Erreur lors de la suppression de la wishlist locale', e);
          }
        } catch (e) {
          console.error('Erreur lors du chargement de la wishlist depuis le serveur', e);
        }
      } else {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            const parsed = JSON.parse(stored) as number[];
            if (Array.isArray(parsed)) {
              setIds(parsed);
            } else {
              setIds([]);
            }
          } else {
            setIds([]);
          }
        } catch (e) {
          console.error('Erreur lors du chargement de la wishlist depuis le localStorage', e);
        }
      }
    };

    load();
  }, [user, token]);

  useEffect(() => {
    if (!user) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
      } catch (e) {
        console.error('Erreur lors de la sauvegarde de la wishlist dans le localStorage', e);
      }
    }
  }, [ids, user]);

  const toggle = (id: number) => {
    if (user && token) {
      (async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/me/wishlist/toggle`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ productId: id }),
          });

          if (!res.ok) {
            console.error("Erreur lors de la mise à jour de la wishlist sur le serveur");
            return;
          }

          const data = await res.json();
          const productIds = Array.isArray(data) ? data : data.productIds;
          if (Array.isArray(productIds)) {
            setIds(productIds.map((x) => Number(x)));
          }
        } catch (e) {
          console.error('Erreur lors de la mise à jour de la wishlist sur le serveur', e);
        }
      })();
      return;
    }

    setIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isInWishlist = (id: number) => ids.includes(id);

  const clear = () => {
    setIds([]);
    if (!user) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.error('Erreur lors de la suppression de la wishlist dans le localStorage', e);
      }
    }
  };

  const value: WishlistContextValue = {
    ids,
    toggle,
    isInWishlist,
    clear,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    throw new Error('useWishlist doit être utilisé dans un WishlistProvider');
  }
  return ctx;
};
